import React, { useState } from 'react';
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Link } from 'react-router-dom';
import { colorPalette } from '@qualifio/ui-commons';
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog';
import { getMenu } from '@loyalty/shared-common';
import { useTranslation } from 'react-i18next';
import { Router } from '../../services';
import {
  customKeysIcon,
  domainsManagementIcon,
  eventReportsIcon,
  integrationsIcon,
  interactionsIcon,
  loginPagesIcon,
  pushRulesIcon,
  testUsersIcon,
} from '../icons';
import { useAccounts } from '../../accounts/AccountsContext';
import { useFeatures } from '../../features/FeaturesContext';

export function SettingsDropdown(): React.ReactElement {
  const { currentAccount } = useAccounts();
  const { features } = useFeatures();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => () => {
    setAnchorEl(null);
  };

  const icons: Record<string, IconDefinition> = {
    'fa-brackets-curly': customKeysIcon,
    'fa-heart-pulse': eventReportsIcon,
    'fa-router': domainsManagementIcon,
    'fa-sitemap': integrationsIcon,
    'fa-upload': pushRulesIcon,
    'fa-user-secret': testUsersIcon,
    'fa-right-to-bracket': loginPagesIcon,
    'fa-receipt': interactionsIcon,
  };

  const items = getMenu({ features, t, legacy: true }).map(menuItem => ({
    ...menuItem,
    href: Router.getPath(menuItem.href, { accountId: currentAccount.id }),
    icon: menuItem.icon ? icons[menuItem.icon] : undefined,
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        borderLeft: `1px solid ${colorPalette.grey.main}`,
      }}
    >
      <Tooltip title={t('topbar.tabs.account.settings')}>
        <Button
          onClick={handleClick}
          variant="text"
          sx={{
            height: '100% !important',
          }}
        >
          <FontAwesomeIcon icon={faCog} size="lg" />
        </Button>
      </Tooltip>
      <Menu
        id="settings-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose()}
      >
        {items.map(item => (
          <CustomMenuItem {...item} onClick={handleClose} key={item.href} />
        ))}
      </Menu>
    </Box>
  );
}

interface Props {
  title?: string;
  disabled?: boolean;
  onClick: () => void;
  href: string;
  icon?: IconDefinition;
  label: string;
}

function CustomMenuItem({
  title = '',
  disabled = false,
  onClick,
  href,
  icon = undefined,
  label,
}: Props) {
  return (
    <Tooltip title={title}>
      <div>
        <MenuItem disabled={disabled} onClick={onClick} component={Link} to={href}>
          <ListItemIcon>{icon && <FontAwesomeIcon icon={icon} size="lg" />}</ListItemIcon>
          <ListItemText>{label}</ListItemText>
        </MenuItem>
      </div>
    </Tooltip>
  );
}
